import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { Experience } from 'src/app/shared/models/personnel';
import { CommonService } from 'src/app/shared/services/common.service';
import { PersonalService } from 'src/app/shared/services/personal.service';

@Component({
  selector: 'app-experience-crud',
  templateUrl: './experience-crud.component.html',
  styleUrls: ['./experience-crud.component.scss'],
})
export class ExperienceCrudComponent implements OnInit {
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @Input() experience: Experience;
  @Input() context: Context;
  @Input() experiences: Experience[] = [];

  public locationTypes: string[] = [];
  public employmentTypes: string[] = [];
  public experienceForm: FormGroup;
  public submitted = false;
  private modified = false;
  selectedFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private personnalService: PersonalService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.setExperienceForm(this.experience);

    this.personnalService.getLocationTypes().subscribe({
      next: (types: string[]) => (this.locationTypes = types),
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || 'Failed to load location types');
      },
    });

    this.personnalService.getEmploymentTypes().subscribe({
      next: (types: string[]) => (this.employmentTypes = types),
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || 'Failed to load employment types');
      },
    });
  }

  public setExperienceForm(experience?: Experience) {
    if (!experience) {
      experience = {
        id: null,
        title: '',
        company: '',
        startDate: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
        endDate: '',
        location: '',
        documentUrl: '',
        description: '',
        verificationStatus: 'PENDING',
        locationType: 'REMOTE',
        employmentType: 'TRAINEE',
        employeeId: this.context?.employeeId,
        employeeName: this.context?.employeeName,
        disabled: false,
      } as Experience;
    }
    this.experienceForm = this.fb.group({
      id: this.fb.control(experience.id),
      title: this.fb.control(experience.title, [Validators.required, Validators.pattern(RegExp.name)]),
      company: this.fb.control(experience.company, [Validators.required, Validators.pattern(RegExp.name)]),
      startDate: this.fb.control(
        this.datePipe.transform(experience?.startDate || '', 'yyyy-MM-dd'),
        [Validators.required]
      ),
      endDate: this.fb.control(this.datePipe.transform(experience?.endDate || '', 'yyyy-MM-dd'), [Validators.required]),
      location: this.fb.control(experience.location, [Validators.required, Validators.pattern(RegExp.name)]),
      description: this.fb.control(experience.description, [Validators.pattern(RegExp.label)]),
      locationType: this.fb.control(experience.locationType, [Validators.required]),
      employmentType: this.fb.control(experience.employmentType, [Validators.required]),
      employeeId: this.fb.control(experience.employeeId, [Validators.required]),
      verificationStatus: this.fb.control(experience.verificationStatus),
      employeeName: this.fb.control(experience.employeeName),
      disabled: this.fb.control(experience.disabled || false),
      documentUrl: this.fb.control(experience.documentUrl),
    });
    this.experienceForm.valueChanges.subscribe(() => (this.modified = true));
  }

  get f() {
    return this.experienceForm.controls;
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.selectedFile = input.files[0];
      this.modified = true;
      this.experienceForm.patchValue({ documentUrl: this.selectedFile.name });
    }
  }

  public saveExperience(inProgress?: { value: boolean }): void {
    this.submitted = true;
    if (!this.experienceForm.valid) {
      return;
    }
    const experience = this.experienceForm.value as Experience;
    if (
      this.experiences.some(
        (t) => t.title.toLowerCase() === experience.title.toLowerCase() && t.id !== experience.id
      )
    ) {
      this.commonService.setToastr(
        400,
        `Cannot ${experience.id ? 'update' : 'add'} the record. ${experience.title} already exists.`
      );
      return;
    }
    if (!this.modified) {
      this.activeModal.close(false);
      return;
    }

    inProgress.value = true;

    if (experience.id) {
      this.personnalService.updateExperience(experience.id, experience).subscribe({
        next: (response: Message) => {
          if (response) {
            this.passEntry.emit(response);
            this.activeModal.close(response);
            this.commonService.setToastr(200, response.text);
            if (this.selectedFile) {
              this.uploadDocument(experience.id, this.selectedFile);
            }
          } else {
            inProgress.value = false;
            this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
          }
        },
        error: (error: HttpErrorResponse) => {
          inProgress.value = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        },
      });
    } else {
      this.personnalService.createExperience(experience).subscribe({
        next: (response: Message) => {
          if (response) {
            this.passEntry.emit(response);
            this.activeModal.close(true);
            this.commonService.setToastr(200, response.text);
            if (response.payload && this.selectedFile) {
              this.uploadDocument(response.payload, this.selectedFile);
            }
          } else {
            inProgress.value = false;
            this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
          }
        },
        error: (error: HttpErrorResponse) => {
          inProgress.value = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        },
      });
    }
  }

  private uploadDocument(experienceId: number, file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('experienceId', experienceId.toString());
    this.personnalService.uploadExperienceFile(experienceId, formData).subscribe({
      next: (response: any) => {
        this.passEntry.emit(response);
        if (response.payload?.documentUrl) {
          this.experienceForm.patchValue({ documentUrl: response.payload.documentUrl });
          this.passEntry.emit({ ...this.experienceForm.value, documentUrl: response.payload.documentUrl });
        }
        this.commonService.setToastr(200, 'Document uploaded successfully.');
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || 'Failed to upload document.');
      },
    });
  }
}